import { IconChevronLeft, IconChevronRight, IconMenu } from "@tine/designsystem-icons/sharp";
import { cn } from "@tine/designsystem-utils";
import isNil from "lodash-es/isNil";
import { observer } from "mobx-react-lite";
import { Link, useLocation } from "wouter";
import NotificationsBadge from "../../account/notifications/NotificationsBadge";
import { sendInternalLinkClick } from "../../common/tracking";
import type { CategoryInfo } from "../../common/types/categoryTypes";
import { titleCase } from "../../common/utils";
import { useMenuItems } from "../../common/utils/menuItems";
import Button from "../../components/Button";
import SlideFromSide from "../../components/common/slidefromside/SlideFromSide";
import Loading from "../../components/Loading";
import StoreLogoComponent from "../../components/StoreLogoComponent";
import authStore from "../../stores/auth/authStore";
import loginState from "../../stores/auth/loginState";
import categoryStore from "../../stores/categories/categoryStore";
import contentStore from "../../stores/cms/contentStore";
import notificationsStore from "../../stores/notifications/notificationsStore";
import { getNotificationsTrackingText } from "../../stores/notifications/notificationsUtils";

const hasChildren = (cat: CategoryInfo) => !isNil(cat.children) && cat.children.length > 0;

const drillDownTree = (menuItems: CategoryInfo[], path: string[]): CategoryInfo | undefined => {
   // Make sure this doesn't crash if called without data
   if (path.length === 0) {
      return undefined;
   }

   const [lookForKey, ...rest] = path;
   const foundItem = menuItems.find((mi) => mi.url_key === lookForKey);

   // No more path to process, we found the item.
   if (rest.length === 0) {
      return foundItem;
   }

   if (isNil(foundItem)) {
      return undefined;
   }

   return drillDownTree(foundItem.children, rest);
};

const itemCN = cn("flex cursor-pointer font-bold");

const RootMenu = observer(() => {
   const path = categoryStore.mobile.path;

   return (
      <ul className="flex flex-col gap-3">
         {/* TODO: This should probably be a Link, right? */}
         {categoryStore.categories.map((cat) => (
            <li className={itemCN} key={cat.id} onClick={() => path.push(cat.url_key)} onKeyDown={() => path.push(cat.url_key)}>
               {titleCase(cat.name)} {hasChildren(cat) && <IconChevronRight className="mr-4" />}
            </li>
         ))}
         {contentStore.mainMenuItems.map((menuItem) => (
            <li className={itemCN} key={menuItem.id}>
               <Link to={menuItem.url} onClick={() => categoryStore.showMenu(false)}>
                  {menuItem.title}
               </Link>
            </li>
         ))}
      </ul>
   );
});

const SubMenu = observer(() => {
   const path = categoryStore.mobile.path;
   const selectedItem = drillDownTree(categoryStore.categories, path);
   const [_, redirectTo] = useLocation();

   const handleClick = (cat: CategoryInfo) => {
      if (!hasChildren(cat)) {
         categoryStore.showMenu(false);
         redirectTo(cat.fullUrl);
      } else {
         path.push(cat.url_key);
      }
   };

   const goBack = () => {
      categoryStore.mobile.path.pop();
   };

   if (isNil(selectedItem)) {
      return <div>Det oppsto den feil under lasting av kategoriene.</div>;
   }

   const visibleItems = selectedItem.children.filter((c: CategoryInfo) => c.visible);

   return (
      <div>
         <h2 className="mb-4 flex cursor-pointer items-center" onClick={goBack}>
            <IconChevronLeft className="mr-4" /> {titleCase(selectedItem.name)}
         </h2>
         <Link className="cursor-pointer underline" onClick={() => categoryStore.showMenu(false)} to={`/${path.join("/")}.html`}>
            Se alle produkter i {titleCase(selectedItem.name)}
         </Link>

         <ul className="mt-4 flex flex-col gap-3">
            {visibleItems.map((cat: CategoryInfo) => (
               <li
                  className="flex cursor-pointer items-center justify-between font-bold"
                  key={cat.id}
                  onClick={() => handleClick(cat)}
               >
                  {titleCase(cat.name)} {hasChildren(cat) && <IconChevronRight />}
               </li>
            ))}
         </ul>
      </div>
   );
});

const MobileCategories = () => {
   const [_, redirectTo] = useLocation();
   const menuItems = useMenuItems();

   const showSwitchCompany = authStore.companies && authStore.companies.length > 1;
   const newPromotionArticlesCount = contentStore.getNewPromotionArticlesCount();
   const notificationsCount = notificationsStore.notifications.data.length;

   if (categoryStore.categories.length === 0) {
      return <Loading />;
   }

   const hideMenu = () => {
      categoryStore.showMenu(false);
   };

   const activateSwitchCompany = (e: React.MouseEvent) => {
      loginState.transitionTo("COMPANY_SELECTION");
      e.preventDefault();
   };

   const mobileMenuHeader = () => <StoreLogoComponent className="ml-2" placement="searchbar" />;

   const menuBodyClassName = "m-2 flex min-w-[80vw] flex-col";
   const borderClassName = "border-b border-ink-border-subtle pb-4 mb-4";

   const logout = () => {
      if (authStore.isPunchoutSession()) {
         loginState.transitionTo("NOT_LOGGED_IN");
         window.location.replace("/");
      } else {
         redirectTo("/");
         loginState.transitionTo("NOT_LOGGED_IN");
      }
      hideMenu();
   };

   return (
      <>
         <Button
            variant="tertiary"
            icon={<IconMenu />}
            onClick={() => categoryStore.showMenu(true)}
            trackingName="Vis mobilmeny"
         />
         <SlideFromSide renderHeader={mobileMenuHeader} show={categoryStore.mobile.visible} onHide={() => hideMenu()}>
            <div className={menuBodyClassName}>
               <div className={borderClassName}>{categoryStore.mobile.path.length === 0 ? <RootMenu /> : <SubMenu />}</div>
               {authStore.isLoggedIn() && (
                  <div className={borderClassName}>
                     <ul className="flex flex-col gap-3">
                        {menuItems.map((menuItem) => (
                           <li key={menuItem.name} onClick={hideMenu}>
                              <Link
                                 to={menuItem.link}
                                 className={cn("flex gap-1 items-center", { underline: menuItem.isActive })}
                                 onClick={() =>
                                    sendInternalLinkClick(
                                       menuItem.link,
                                       "Mobile menu",
                                       getNotificationsTrackingText(menuItem.name, notificationsCount, newPromotionArticlesCount)
                                    )
                                 }
                              >
                                 <span>{menuItem.name}</span>
                                 <span>
                                    {menuItem.name === "Varslinger" && <NotificationsBadge count={notificationsCount} />}
                                 </span>
                                 <span>
                                    {menuItem.name === "Kampanjer" && <NotificationsBadge count={newPromotionArticlesCount} />}
                                 </span>
                              </Link>
                           </li>
                        ))}

                        {authStore.isLoggedIn() && (
                           <li>
                              <button type="button" onClick={logout} data-testid="logout-button">
                                 Logg ut
                              </button>
                           </li>
                        )}
                     </ul>
                  </div>
               )}
               {showSwitchCompany && (
                  <div className={borderClassName}>
                     <button type="button" onClick={activateSwitchCompany}>
                        Bytt firma
                     </button>
                  </div>
               )}
               {authStore.isLoggedIn() && (
                  <div className="text-sm">
                     Logget inn som
                     <br />
                     {authStore.currentCompany} {authStore.getCurrentCompany()?.name ?? ""}
                  </div>
               )}
            </div>
         </SlideFromSide>
      </>
   );
};

export default observer(MobileCategories);
